import { debounce } from 'lodash';
import { useState, useRef } from 'react';
import { StringHelper } from '@act/shared/utilities';
import { FlexContainer } from '../../containers/ContentContainers/FlexContainer';
import { TaggableChip } from './TaggableChip';

export type FilterChoices<ValueType> = { label: string; value: ValueType }[];

interface FilterChipsProps<ValueType> {
  filterChoices: FilterChoices<ValueType>;
  setSelectedFilters: (selectedFilters: ValueType[]) => void;
}

export const FilterChips = <ValueType,>({
  filterChoices,
  setSelectedFilters,
}: FilterChipsProps<ValueType>) => {
  const [selectedIndices, setSelectedIndices] = useState<number[]>([0]);
  const chipRefs = useRef<(HTMLDivElement | null)[]>([]);

  function selectChip(index: number) {
    let newIndices: number[];

    if (index === 0) {
      if (selectedIndices.includes(0)) {
        return;
      } else {
        newIndices = [0];
      }
    } else {
      const adjustedIndex = index;

      if (selectedIndices.includes(adjustedIndex)) {
        newIndices = selectedIndices.filter(i => i !== adjustedIndex);
        if (newIndices.length === 0) {
          newIndices = [0];
        }
      } else {
        newIndices = [...selectedIndices.filter(i => i !== 0), adjustedIndex];
      }
    }

    setSelectedIndices(newIndices);

    const filterValues = newIndices
      .filter(i => i !== 0)
      .map(i => filterChoices[i - 1].value);

    selectedFiltersHandler(filterValues);

    chipRefs.current[index]?.blur();
  }

  function resetChips() {
    setSelectedIndices([0]);
    selectedFiltersHandler([]);
    chipRefs.current[0]?.blur();
  }

  const selectedFiltersHandler = debounce((filterValues: ValueType[]) => {
    setSelectedFilters(filterValues);
  }, 250);

  return (
    <FlexContainer
      direction="row"
      gap={1}
      height="auto"
      width="100%"
      noWrap
      sx={{
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflowX: 'auto',
        overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch',
        '&::-webkit-scrollbar': {
          height: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          borderRadius: '3px',
        },
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
        flexWrap: 'nowrap',
      }}
    >
      <TaggableChip
        ref={el => (chipRefs.current[0] = el)}
        label="All"
        size="medium"
        color="neutral"
        variant="filled"
        isSelected={selectedIndices.includes(0)}
        onClick={resetChips}
        sx={{ flexShrink: 0 }}
      />
      {filterChoices.map((choice, index) => {
        const chipIndex = index + 1;
        return (
          <TaggableChip
            ref={el => (chipRefs.current[chipIndex] = el)}
            key={`${choice.label}-${index}`}
            label={StringHelper.sentenceCase(choice.label)}
            size="medium"
            color="neutral"
            variant="filled"
            isSelected={selectedIndices.includes(chipIndex)}
            onClick={() => selectChip(chipIndex)}
            sx={{ flexShrink: 0 }}
          />
        );
      })}
    </FlexContainer>
  );
};
